import { clsx } from "clsx";
import React, { useMemo } from "react";

import { Price } from "@bared/ui";

import CountdownTimer from "@components/CountdownTimer/CountdownTimer";
import { Reviews } from "@components/Reviews/Reviews";
import ReviewsInline from "@components/Reviews/ReviewsInline";
import { Sections } from "@components/Sections/Sections";
import tw, { styled } from "twin.macro";
import RichText from "../RichText";
import BuyNowPayLater from "./BuyNowPayLater/BuyNowPayLater";
import FlashSale from "./FlashSale/FlashSale";
import { Images } from "./Images/Images";
import { ServiceHighlights } from "./ServiceHighlights/ServiceHighlights";
import { Title, TitleWrapper } from "./Shared";
import { withProduct } from "./withProduct";

import { useSite } from "~/hooks/useSite";
import { useBetweenTime } from "~/hooks/useTime";
import { useRichText } from "~/hooks/useRichText";
import { useSettings } from "~/hooks/useSettings";

import { useProduct } from "~/templates/product/useProduct";

import { Features } from "~/components/Product/Features/Features";

import {
  Link,
  Callout,
  Container,
  Accordion,
  Breadcrumbs,
  ProductLayout,
  ShopifyRichText,
  AccordionSection,
  type AccordionSectionProps,
  Badge,
} from "@bared/ui";

const SubTitle = tw.p`
  text-body leading-body text-caption
`;
const InlineReviews = styled(ReviewsInline)`
  ${tw`whitespace-nowrap`}
  & .ruk-rating-snippet-count {
    text-decoration: underline;
    text-underline-offset: 0.1rem;
  }
`;
const Description = styled(RichText)`
  ${tw`block mb-mini`}
  p {
    ${tw`text-left`}
  }
`;
const Message = styled(Description)`
  ${tw`italic md:-mt-midi`}
  p {
    ${tw`pb-0 text-[13px]`}
  }
`;

const SectionsWrapper = tw.div``;

const OutOfStockMessage = styled.div`
  ${tw`bg-white border-l-4 px-midi py-mini border-warning`}

  p {
    ${tw`text-warning leading-body text-body`}
  }

  a {
    ${tw`font-medium underline`}
  }
`;

export const Product = withProduct(
  ({
    additionalContents,
    badge,
    callout,
    content,
    contentByTag,
    description,
    flashSaleDescription,
    flashSaleTitle,
    handle,
    heroImage,
    images,
    isFlashSaleProduct,
    onSale,
    parentCollection,
    paths,
    preorderMessage,
    productGroup,
    product,
    saleTag,
    sectionsRef,
    selectedVariant,
    showTimers,
    sizingAdvice,
    sku,
    subTitle,
    tags,
    video,
    outOfStockMessage,
    shoeCareSection = [],
    howToStyleSection,
    asSeenOnSection,
    productName,
    productGender,
    productStatus,
    productSpecificContent,
    productRecommendations,
    imageGallery,
    shoeCareGuide,
    sustainableFeatures,
    swatches,
    siblingColours,
    reviewsSummary,
    children,
  }) => {
    const site = useSite();

    const { shopifyProduct, isFinalSale } = useProduct();

    const showMensPromo =
      productGender === "Mens" &&
      !product?.variants?.[0]?.compareAtPriceV2 &&
      ["au", "nz"].includes(site.handle) &&
      useBetweenTime("2025-01-10T09:00:00+11:00", "2025-01-26T23:59:59+11:00");

    const { parseContent } = useRichText();
    const { tabbedContent } = useSettings();
    /** Mapped array of accordion sections for display in the description area. */
    const accordionSections = useMemo<AccordionSectionProps[]>(() => {
      // Merge generic content together with product specific content.
      const mergedAdditionalContent = [
        ...(tabbedContent?.tabs ?? []),
        ...additionalContents,
      ];
      // Map an array of accordion sections.
      const mergedAccordianSections = mergedAdditionalContent
        .filter(
          (additionalContent) =>
            !additionalContent.excludeUrls ||
            !new RegExp(additionalContent.excludeUrls).test(handle),
        )
        .map(
          (additionalContent): AccordionSectionProps => ({
            id: additionalContent._key,
            title: additionalContent.title ?? "",
            children: (
              <>
                {additionalContent.title === "Shoe Care" &&
                shoeCareSection.length > 0 ? (
                  <>{shoeCareSection}</>
                ) : null}
                {!!additionalContent.displayAbove &&
                  !!additionalContent.content?.content && (
                    <div className="rte">
                      {additionalContent.content.content.map((item) =>
                        parseContent(item),
                      )}
                    </div>
                  )}
                {!!additionalContent.useShopifyDescription ? (
                  <RichText html={description} />
                ) : null}
                {!additionalContent.useShopifyDescription &&
                  !additionalContent.displayAbove &&
                  !!additionalContent.content?.content && (
                    <div className="rte">
                      {additionalContent.content.content.map((item) =>
                        parseContent(item),
                      )}
                    </div>
                  )}
                {additionalContent.features && (
                  <Features
                    features={additionalContent.features}
                    tags={tags}
                    description={description}
                  />
                )}
              </>
            ),
          }),
        );
      // If no additional content in Sanity, use the description from shopify
      if (mergedAccordianSections.length === 0 && description) {
        return [
          {
            id: "shopify_features",
            title: "Features",
            children: <RichText html={description} />,
          },
        ];
      }

      /** Shoe Care Section - Sourced from Product Metadata */
      if (
        mergedAccordianSections &&
        mergedAccordianSections?.length > 0 &&
        shoeCareGuide?.value
      ) {
        mergedAccordianSections.push({
          id: shoeCareGuide.id || "shoe_care",
          title: shoeCareGuide.title || "Shoe Care",
          children: (
            <>
              {shoeCareSection && shoeCareSection.length > 0
                ? shoeCareSection
                : null}
              <ShopifyRichText content={shoeCareGuide.value} />
            </>
          ),
        });
      }
      /** Sustainabile Features Section - Sourced from Product Metadata */
      if (
        mergedAccordianSections &&
        mergedAccordianSections?.length > 0 &&
        sustainableFeatures?.value
      ) {
        mergedAccordianSections.push({
          id: sustainableFeatures.id || "sustainable_features",
          title: sustainableFeatures.title || "Sustainable Features",
          children: <ShopifyRichText content={sustainableFeatures.value} />,
        });
      }

      return mergedAccordianSections;
    }, [
      tabbedContent,
      additionalContents,
      handle,
      shoeCareSection,
      sustainableFeatures,
    ]);
    const notify = useMemo(
      () => ["Core", "Seasonal"].includes(productStatus),
      [productStatus],
    );

    return (
      <>
        <div className="bg-neutral">
          <Container
            className={clsx(!imageGallery && "pb-maxi")}
            maxWidth={!!imageGallery ? "default" : "xl"}
          >
            {!imageGallery && (
              <Breadcrumbs className="mb-maxi hidden md:flex">
                {paths
                  .map((path) => {
                    if (!path.url) return;
                    return (
                      <Link href={path.url} variant="link">
                        {path.title}
                      </Link>
                    );
                  })
                  .filter(Boolean)}
              </Breadcrumbs>
            )}
            <div className="space-y-mini">
              <ProductLayout
                legacy={!imageGallery}
                stickyOffsetClassName={
                  imageGallery ? "top-[122px]" : "top-[152px]"
                }
                imageGallery={
                  imageGallery ?? (
                    <Images
                      image={heroImage}
                      images={images}
                      video={video}
                      badge={badge}
                    />
                  )
                }
              >
                <div
                  className={clsx(
                    "flex flex-col space-y-mini md:space-y-maxi",
                    !!imageGallery && "py-mini md:py-maxi",
                  )}
                >
                  {!!imageGallery && (
                    <Breadcrumbs>
                      {paths
                        .map((path) => {
                          if (!path.url) return;
                          return (
                            <Link href={path.url} variant="link">
                              {path.title}
                            </Link>
                          );
                        })
                        .filter(Boolean)}
                    </Breadcrumbs>
                  )}
                  <div className="space-y-micro">
                    <TitleWrapper className="flex items-start justify-between">
                      <div className="flex flex-col">
                        <Title className="mb-micro">{productName}</Title>
                        <Price
                          price={shopifyProduct.priceRange.maxVariantPrice}
                          compareAtPrice={
                            shopifyProduct.compareAtPriceRange.maxVariantPrice
                          }
                        />
                      </div>
                      <div className="flex flex-col items-end gap-micro">
                        <InlineReviews productGroup={productGroup} />
                        {!!isFinalSale && (
                          <Badge variant="warning" size="small">
                            Final Sale
                          </Badge>
                        )}
                      </div>
                    </TitleWrapper>
                    <BuyNowPayLater
                      onSale={onSale}
                      saleTag={saleTag}
                      presentmentPrice={
                        selectedVariant?.priceV2 ||
                        product?.variants?.[0]?.priceV2
                      }
                      compareAtPrice={
                        selectedVariant?.compareAtPriceV2 ||
                        product?.variants?.[0]?.compareAtPriceV2
                      }
                    />
                    {!!subTitle ? (
                      <SubTitle>
                        <span className="font-medium">Colour: </span>
                        {subTitle}
                      </SubTitle>
                    ) : null}
                    {swatches && <>{swatches}</>}
                    {isFlashSaleProduct && (
                      <FlashSale
                        title={flashSaleTitle}
                        description={flashSaleDescription}
                      />
                    )}
                  </div>
                  <div>
                    {notify || product?.availableForSale ? (
                      <>
                        {selectedVariant?.isPreOrder &&
                          preorderMessage?.length > 0 && (
                            <Message html={`<p>${preorderMessage}</p>`} />
                          )}
                      </>
                    ) : (
                      <OutOfStockMessage>{outOfStockMessage}</OutOfStockMessage>
                    )}
                    <div className="mb-mini">{children}</div>
                    {showTimers && (
                      <CountdownTimer
                        layout={"product"}
                        collection={parentCollection}
                      />
                    )}
                    {showMensPromo && (
                      <Callout
                        size="large"
                        variant="secondary"
                        className="mb-mini"
                      >
                        <p>
                          Buy 1 Get 50% Off The 2nd. Discount applied at
                          checkout.
                        </p>
                      </Callout>
                    )}
                    {(!!callout || !!sizingAdvice) && (
                      <Callout
                        className="mb-mini"
                        variant="secondary"
                        size="large"
                      >
                        {!!callout && (
                          <ShopifyRichText
                            className="mb-micro"
                            content={callout}
                          />
                        )}
                        {!!sizingAdvice && (
                          <ShopifyRichText content={sizingAdvice} />
                        )}
                      </Callout>
                    )}
                    {!!accordionSections[0] && (
                      <Accordion autoClose>
                        {accordionSections.map((accordionSection) => (
                          <AccordionSection
                            {...accordionSection}
                            key={accordionSection.id}
                          />
                        ))}
                      </Accordion>
                    )}
                    <ServiceHighlights />
                  </div>
                </div>
              </ProductLayout>
              {howToStyleSection && <>{howToStyleSection}</>}
              {productSpecificContent && (
                <div className="-mx-mini md:-mx-maxi">
                  <Sections page={productSpecificContent} />
                </div>
              )}
              {asSeenOnSection && <>{asSeenOnSection}</>}
            </div>

            <Reviews
              productGroup={productGroup}
              productHandle={handle}
              sku={sku}
              gender={productGender}
              siblingColours={siblingColours}
              reviewsSummary={reviewsSummary}
            />
          </Container>
        </div>

        <Container>
          {productRecommendations && <>{productRecommendations}</>}
        </Container>

        {contentByTag.content && <Sections page={contentByTag} />}

        <SectionsWrapper ref={sectionsRef}>
          {content.content && <Sections page={content} />}
        </SectionsWrapper>
      </>
    );
  },
);

export default Product;
